/**
 * Directional hex templates — LINE and CONE. The hex analogue of `square.ts`'s
 * `ray`/`cone`: a one-wide line and a 90°-spread wedge, both emanating from an
 * apex in the aimed direction, snapped to one of the six hex {@link DIRECTIONS}.
 * Pure cube-integer
 * math, render-free — the geometry {@link makeWorld}'s `linePattern`/`conePattern`
 * bind for hex encounters, mirroring how the square world binds `ray`/`cone`.
 *
 * The bearing test uses the standard 120° cube→Euclidean embedding (the three cube
 * axes as unit vectors 120° apart): a cube vector `(q,r,s)` maps to Cartesian
 * `(3q/2, -√3(q+2r)/2)`, so the Euclidean dot product of two cube offsets is
 * proportional to `dirScore` below (the shared `3/2` factor cancels in every cosine
 * we compute). That gives a real, deterministic angle between an offset and the aim
 * axis without importing any pixel `Layout`.
 */
import { type Hex, DIRECTIONS, add, scale, subtract } from "./coord";

/**
 * A rank/angle score proportional to the Euclidean dot product of two cube vectors
 * under the 120° embedding (the shared `3/2` scale is dropped — it cancels in every
 * cosine and never changes an argmax). `dirScore(v, v)` is proportional to `|v|²`,
 * so `dirScore(a,b) / √(dirScore(a,a)·dirScore(b,b))` is exactly `cos∠(a,b)`.
 */
function dirScore(a: Hex, b: Hex): number {
  return 2 * a.q * b.q + a.q * b.r + a.r * b.q + 2 * a.r * b.r;
}

/**
 * Snap the aim `apex → toward` to the nearest of the six hex {@link DIRECTIONS} (the
 * one whose bearing is closest to the aim vector), lowest-index on an exact tie for
 * determinism. `null` when `toward === apex` (no aim). The basis of {@link hexRay} /
 * {@link hexCone}.
 *
 * **The hex templates snap; the square ones aim freely.** That asymmetry is
 * deliberate: the free-aim hex twin is cube-lerp + cube-rounding (with an epsilon
 * nudge to break corner ties), not the integer Bresenham DDA `square.ts` uses, and
 * the default grid is `2d-square` (`DEFAULT_GRID_MODE`, `shared/src/index.ts`) — so
 * nobody plays on the snapped path today. TODO: give hex free aiming when hex
 * encounters become real.
 */
export function hexDirectionToward(apex: Hex, toward: Hex): Hex | null {
  const delta = subtract(toward, apex);
  if (delta.q === 0 && delta.r === 0 && delta.s === 0) return null;
  let best: Hex = DIRECTIONS[0]!;
  let bestScore = -Infinity;
  for (const d of DIRECTIONS) {
    const sc = dirScore(delta, d);
    if (sc > bestScore) {
      bestScore = sc;
      best = d;
    }
  }
  return best;
}

/**
 * A **LINE** template on hex: the `n` cells (`n >= 1`) emanating from `apex` in the
 * {@link hexDirectionToward} direction of `toward`, one cell wide, **excluding** the
 * apex — the hex twin of square {@link import("./square").ray}. The Lancer Line
 * projects its full length in the aimed direction regardless of cursor distance, so
 * this snaps the aim to one of six rays and walks `n` cells out. Empty when `n <= 0`
 * or `toward === apex`. Pure cube-integer math.
 */
export function hexRay(apex: Hex, toward: Hex, n: number): Hex[] {
  if (n <= 0) return [];
  const dir = hexDirectionToward(apex, toward);
  if (!dir) return [];
  const out: Hex[] = [];
  for (let i = 1; i <= n; i++) out.push(add(apex, scale(dir, i)));
  return out;
}

/** cos(45°) — the half-angle of the 90° cone, matching square `cone`'s `|perp| <= along`. */
const COS_45 = Math.SQRT1_2;

/**
 * A **CONE** template on hex: the wedge of cells emanating from `apex` toward
 * `toward`, out to grid length `n` (`n >= 1`), spreading 45° to each side of the
 * snapped aim axis (a 90° cone — the same half-angle square {@link
 * import("./square").cone} uses). A cell is included when it sits within `n` steps of
 * the apex (apex excluded) and its bearing from the apex is within 45° of the aim
 * direction. Empty when `n <= 0` or `toward === apex`. Pure cube-integer math.
 */
export function hexCone(apex: Hex, toward: Hex, n: number): Hex[] {
  if (n <= 0) return [];
  const dir = hexDirectionToward(apex, toward);
  if (!dir) return [];
  const dirLen2 = dirScore(dir, dir);
  const out: Hex[] = [];
  for (let dq = -n; dq <= n; dq++) {
    const rLo = Math.max(-n, -dq - n);
    const rHi = Math.min(n, -dq + n);
    for (let dr = rLo; dr <= rHi; dr++) {
      if (dq === 0 && dr === 0) continue; // exclude the apex
      const off: Hex = { q: dq, r: dr, s: -dq - dr };
      const cos = dirScore(off, dir) / Math.sqrt(dirScore(off, off) * dirLen2);
      if (cos >= COS_45 - 1e-9) out.push(add(apex, off));
    }
  }
  return out;
}
